import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { SupplyCartLine } from "@dentacore/shared-types";

import { clearCart, listCart, removeCartLine, saveCartLine } from "../api/suppliers";
import { locate } from "../api/located";
import { acrossRows, useShown } from "../shell/locations";

/**
 * **The cart, one per location, shared by everyone ordering there** (SU-D17).
 *
 * It lived in this browser's `localStorage` until 2026-10-16, so a half-built
 * cart could not be handed to a colleague or finished on another computer. It
 * is on the server now: every change is saved as it is made, and the cart is
 * read again when this tab comes back into view and every half minute while it
 * is open, so a colleague's lines arrive without anybody reloading.
 *
 * The server re-checks everything at checkout (the price, the cheapest-supplier
 * rule, an item already on order), so a stale line is refused rather than trusted.
 */
export interface CartLine {
  inventoryItemId: string;
  itemName: string;
  supplierPriceId: string;
  supplierId: string;
  supplierName: string;
  pack: string;
  usesPerPack: number;
  priceCents: number;
  /** Among the cheapest, worked out by the server when the cart is read. */
  cheapest: boolean;
  /** A string while being typed, like every number field here. */
  packs: string;
  reason: string;
  /** The location whose stock the line is for, when several are shown. */
  at?: string;
}

interface Cart {
  lines: readonly CartLine[];
  has: (inventoryItemId: string) => boolean;
  add: (line: CartLine) => void;
  update: (inventoryItemId: string, change: Partial<Pick<CartLine, "packs" | "reason">>) => void;
  remove: (inventoryItemId: string) => void;
  clear: () => void;
  /** Read the cart again — after a checkout, which empties what it ordered. */
  reload: () => void;
}

const CartContext = createContext<Cart>({
  lines: [],
  has: () => false,
  add: () => undefined,
  update: () => undefined,
  remove: () => undefined,
  clear: () => undefined,
  reload: () => undefined,
});

/** How often an open cart picks up a colleague's changes. */
const REFRESH_MS = 30_000;
/** How long typing settles before it is saved. */
const SETTLE_MS = 400;

const fromServer = (line: SupplyCartLine & { at?: string }): CartLine => ({
  ...(line.at === undefined ? {} : { at: line.at }),
  inventoryItemId: line.inventoryItemId,
  itemName: line.itemName,
  supplierPriceId: line.supplierPriceId,
  supplierId: line.supplierId,
  supplierName: line.supplierName,
  pack: line.pack,
  usesPerPack: line.usesPerPack,
  priceCents: line.priceCents,
  cheapest: line.cheapest,
  packs: String(line.packs),
  reason: line.reason,
});

export function CartProvider({
  userId,
  branchId,
  enabled,
  children,
}: {
  userId: string | null;
  branchId: string | null;
  /**
   * The clinic has Supply and this person may see stock. Otherwise there is no
   * cart and nothing is asked for — it used to poll every 30 seconds for every
   * clinic and take a refusal each time (QA-16).
   */
  enabled: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const [lines, setLines] = useState<readonly CartLine[]>([]);
  /** Edits typed but not saved yet, by item — a refresh must not undo them. */
  const pending = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // Every location shown: one cart holding each location's lines.
  const places = useShown();
  const read = useCallback(
    (): Promise<readonly (SupplyCartLine & { at?: string })[]> =>
      places.many
        ? acrossRows(
            places.ids,
            () => listCart(),
            (line, at) => locate(line.inventoryItemId, at),
          )
        : listCart(),
    [places.key],
  );

  const take = useCallback((rows: readonly (SupplyCartLine & { at?: string })[]) => {
    setLines((was) =>
      rows.map((row) => {
        const mine = was.find((l) => l.inventoryItemId === row.inventoryItemId);
        // A line still being typed into keeps what is on screen.
        return mine !== undefined && pending.current.has(row.inventoryItemId)
          ? mine
          : fromServer(row);
      }),
    );
  }, []);

  const reload = useCallback(() => {
    if (!enabled || userId === null || branchId === null) return;
    // No Supply, or no right to see stock: there is no cart, and that is not an error.
    read()
      .then(take)
      .catch(() => setLines([]));
  }, [enabled, userId, branchId, take, read]);

  // A different location is a different cart; then keep it fresh while it is open.
  useEffect(() => {
    setLines([]);
    reload();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") reload();
    }, REFRESH_MS);
    const onVisible = (): void => {
      if (document.visibilityState === "visible") reload();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reload]);

  const value = useMemo<Cart>(
    () => ({
      lines,
      has: (id) => lines.some((l) => l.inventoryItemId === id),
      // One line per item: adding again replaces it with the newly chosen supplier.
      add: (line) => {
        setLines((was) => [...was.filter((l) => l.inventoryItemId !== line.inventoryItemId), line]);
        saveCartLine(line.inventoryItemId, {
          supplierPriceId: line.supplierPriceId,
          packs: /^\d+$/.test(line.packs) ? Math.max(1, Number(line.packs)) : 1,
          reason: line.reason,
        })
          // With several locations, one location's answer is not the whole cart.
          .then((rows) => (places.many ? reload() : take(rows)))
          .catch(reload);
      },
      update: (id, change) => {
        setLines((was) => was.map((l) => (l.inventoryItemId === id ? { ...l, ...change } : l)));
        const packs = change.packs;
        // Half-typed packs ("", "0") are shown but not saved; the cart refuses them at checkout.
        if (packs !== undefined && !(/^\d+$/.test(packs) && Number(packs) >= 1)) return;
        const timers = pending.current;
        const was = timers.get(id);
        if (was !== undefined) clearTimeout(was);
        timers.set(
          id,
          setTimeout(() => {
            timers.delete(id);
            saveCartLine(id, {
              ...(packs === undefined ? {} : { packs: Number(packs) }),
              ...(change.reason === undefined ? {} : { reason: change.reason }),
            })
              .then(take)
              .catch(reload);
          }, SETTLE_MS),
        );
      },
      remove: (id) => {
        setLines((was) => was.filter((l) => l.inventoryItemId !== id));
        removeCartLine(id).catch(reload);
      },
      clear: () => {
        setLines([]);
        clearCart().catch(reload);
      },
      reload,
    }),
    [lines, take, reload, places.many],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): Cart {
  return useContext(CartContext);
}
